import React, { useState, useEffect } from 'react';
import {
  FiClock,
  FiZap,
  FiArrowUpRight,
  FiAlertTriangle,
  FiMic,
  FiVideo,
  FiVolume2,
  FiVideoOff,
  FiAward,
  FiTarget,
  FiMessageCircle,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiX,
  FiCheckCircle,
} from 'react-icons/fi';
import {
  getSessionStats,
  formatMinutes,
  formatSessionMinutesLabel,
  getSubscriptionUpgradeInfo,
  getTimeUntilReset,
} from '../utils/voiceMinutesManager';
import {
  requestSpeakingMicrophone,
  enumerateAudioInputDevices,
  analyzeBackgroundNoise,
  getNoiseGuidance,
  getActiveAudioProtectionFeatures,
} from '../utils/audioEnvironmentUtils';
import './VoiceMinutesWidget.css';

const VoiceMinutesWidget = ({
  compact = false,
  sessionBar = false,
  onUpgradeClick = null,
  isCameraEnabled,
  onCameraToggle,
  onVolumeChange,
  onReadinessChange = null, // Callback when readiness state changes
  onInputDeviceChange = null,
  hideTimeDisplay = false, // Hide the time/minutes display section (for IELTS test mode)
  customTimePanel = null, // Custom panel to show instead of time display
}) => {
  const [stats, setStats] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Audio testing states
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(0);

  // New UI states for collapsible sections
  const [showMinutesDetails, setShowMinutesDetails] = useState(false);

  // Readiness check states
  const [micTested, setMicTested] = useState(false);
  const [audioTested, setAudioTested] = useState(false);
  const [autoCheckComplete, setAutoCheckComplete] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('checking'); // 'checking', 'granted', 'denied', 'prompt'
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [selectedInputDeviceId, setSelectedInputDeviceId] = useState('');
  const [backgroundNoiseLevel, setBackgroundNoiseLevel] = useState('unknown');
  const [noiseGuidance, setNoiseGuidance] = useState(null);

  // Auto-check microphone permissions and audio on mount
  useEffect(() => {
    const autoCheckDevices = async () => {
      try {
        // Check microphone permission status
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const micPermission = await navigator.permissions.query({
              name: 'microphone',
            });
            setPermissionStatus(micPermission.state);

            // Listen for permission changes
            micPermission.onchange = () => {
              setPermissionStatus(micPermission.state);
              if (micPermission.state === 'granted') {
                // Re-run auto check if permission is granted
                testMicrophoneAuto();
              }
            };

            if (micPermission.state === 'granted') {
              // Auto-test microphone if permission already granted
              await testMicrophoneAuto();
            }
          } catch (permErr) {
            // Permission API not supported for microphone, try direct access
            console.log('Permission API not available, trying direct access');
            await testMicrophoneAuto();
          }
        } else {
          // Fallback: try to access microphone directly
          await testMicrophoneAuto();
        }

        // Auto-test audio output (always possible, no permission needed)
        testAudioAuto();

        setAutoCheckComplete(true);
      } catch (error) {
        console.log('Auto-check failed:', error);
        setAutoCheckComplete(true);
        setPermissionStatus('denied');
      }
    };

    // Run auto-check on mount
    autoCheckDevices();
  }, []);

  const loadAudioInputDevices = async () => {
    try {
      const devices = await enumerateAudioInputDevices();
      setAudioInputDevices(devices);
      if (!selectedInputDeviceId && devices.length > 0) {
        const preferred =
          devices.find((device) =>
            /headset|headphone|airpod|earbud|bluetooth/i.test(device.label)
          ) || devices[0];
        setSelectedInputDeviceId(preferred.deviceId);
        if (onInputDeviceChange) {
          onInputDeviceChange(preferred.deviceId);
        }
      }
    } catch (error) {
      console.log('Failed to enumerate audio devices:', error);
    }
  };

  // Silent microphone test - just checks if we can access the mic
  const testMicrophoneAuto = async () => {
    try {
      const stream = await requestSpeakingMicrophone(
        selectedInputDeviceId || undefined
      );
      await loadAudioInputDevices();
      // Successfully got access - mark as tested
      setMicTested(true);
      setPermissionStatus('granted');
      // Stop the stream immediately
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.log('Microphone auto-test failed:', error);
      setPermissionStatus('denied');
      return false;
    }
  };

  // Silent audio test - play a very short, nearly inaudible beep to verify audio context works
  const testAudioAuto = () => {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Very quiet and short - user won't notice
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.01, audioContext.currentTime); // Nearly silent

      oscillator.start();

      // Stop after 50ms - imperceptible
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
        setAudioTested(true);
      }, 50);

      return true;
    } catch (error) {
      console.log('Audio auto-test failed:', error);
      return false;
    }
  };

  // Notify parent when readiness changes
  useEffect(() => {
    if (onReadinessChange) {
      onReadinessChange({
        micTested,
        audioTested,
        isReady: micTested && audioTested,
        inputDeviceId: selectedInputDeviceId,
        backgroundNoiseLevel,
      });
    }
  }, [
    micTested,
    audioTested,
    onReadinessChange,
    selectedInputDeviceId,
    backgroundNoiseLevel,
  ]);

  useEffect(() => {
    let interval;

    const initializeStats = async () => {
      await updateStats();

      // Check if there's an active session to update more frequently
      const currentStats = await getSessionStats();
      const activeSession = currentStats.activeSession;
      const updateInterval = activeSession ? 5000 : 30000; // 5 seconds if active, 30 seconds otherwise

      interval = setInterval(() => {
        updateStats(); // This is fine as setInterval doesn't wait for promises
      }, updateInterval);
    };

    initializeStats();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  // Prevent modal from closing immediately on mount
  useEffect(() => {
    if (showUpgradeModal) {
      // Add a small delay to prevent immediate closing
      const timer = setTimeout(() => {
        // This ensures the modal is properly mounted before handling events
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showUpgradeModal]);

  const updateStats = async () => {
    const currentStats = await getSessionStats();
    setStats(currentStats);
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpgradeClick = () => {
    if (isProcessing) return;

    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 500); // Prevent double clicks

    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      setShowUpgradeModal(true);
    }
  };

  const closeModal = () => {
    if (!isProcessing) {
      setShowUpgradeModal(false);
    }
  };

  const handleInputDeviceChange = async (deviceId) => {
    setSelectedInputDeviceId(deviceId);
    setBackgroundNoiseLevel('unknown');
    setNoiseGuidance(null);
    if (onInputDeviceChange) {
      onInputDeviceChange(deviceId);
    }
  };

  // Test microphone by recording for 5 seconds and playing back
  const testMicrophone = async () => {
    if (isRecording) return;

    try {
      const stream = await requestSpeakingMicrophone(
        selectedInputDeviceId || undefined
      );
      await loadAudioInputDevices();

      const noiseAnalysis = await analyzeBackgroundNoise(stream);
      setBackgroundNoiseLevel(noiseAnalysis.level);
      setNoiseGuidance(getNoiseGuidance(noiseAnalysis.level));

      const recorder = new MediaRecorder(stream);
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        setMicTested(true); // Mark mic as tested

        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());

        // Auto-play the recording
        setTimeout(() => {
          playRecording(audioUrl);
        }, 500);
      };

      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTimeLeft(5);
      recorder.start();

      // Countdown timer
      const countdownInterval = setInterval(() => {
        setRecordingTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            recorder.stop();
            setIsRecording(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please check your permissions.');
    }
  };

  // Play the recorded audio
  const playRecording = (audioUrl) => {
    if (isPlayingBack) return;

    const audio = new Audio(audioUrl || recordedAudio);
    audio.volume = 80 / 100;
    setIsPlayingBack(true);

    audio.onended = () => {
      setIsPlayingBack(false);
    };

    audio.onerror = () => {
      setIsPlayingBack(false);
      console.error('Error playing recorded audio');
    };

    audio.play();
  };

  // Test AI volume with a sample sound
  const testAIVolume = () => {
    if (isPlayingTestSound) return;

    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800 Hz beep
    gainNode.gain.setValueAtTime(100 / 100, audioContext.currentTime);

    setIsPlayingTestSound(true);
    oscillator.start();

    // Stop after 1 second
    setTimeout(() => {
      oscillator.stop();
      setIsPlayingTestSound(false);
      setAudioTested(true); // Mark audio as tested
    }, 1000);
  };

  const upgradeInfo = getSubscriptionUpgradeInfo();

  if (!stats) return null;

  const remainingSessions = stats.remainingSessions ?? stats.remainingMinutes ?? 0;
  const maxSessionsPerDay = stats.maxSessionsPerDay ?? stats.weeklyLimit ?? 5;
  const percentageUsed =
    ((maxSessionsPerDay - remainingSessions) /
      Math.max(maxSessionsPerDay || 1, 1)) *
    100;

  if (compact) {
    return (
      <div
        className={`voice-minutes-compact ${sessionBar ? 'session-bar' : ''} ${remainingSessions < 1 ? 'low-minutes' : ''}`}
      >
        <div className="voice-minutes-compact-content">
          <FiClock size={16} />
          <span>
            {sessionBar
              ? formatSessionMinutesLabel(stats.maxSessionDuration)
              : `${remainingSessions} session${remainingSessions === 1 ? '' : 's'} left today`}
          </span>
          {remainingSessions < 1 && (
            <div className="compact-warning">
              <FiZap size={14} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Calculate readiness status
  const allChecksComplete = micTested && audioTested;
  const checksCount =
    (micTested ? 1 : 0) +
    (audioTested ? 1 : 0) +
    (isCameraEnabled !== undefined ? 1 : 0);
  const totalChecks = 3;

  return (
    <>
      <div className="voice-minutes-widget control-panel redesigned">
        {/* Custom Time Panel (for IELTS test mode) or Voice Minutes Header */}
        {customTimePanel ? (
          customTimePanel
        ) : !hideTimeDisplay ? (
          <div className="voice-minutes-summary">
            <div className="summary-main">
              <div className="summary-time">
                <FiClock size={20} className="summary-icon" />
                <div className="summary-time-info">
                  <span className="summary-remaining">
                    {remainingSessions}
                  </span>
                  <span className="summary-label">sessions left today</span>
                </div>
              </div>
            </div>

            {/* Progress bar - always visible */}
            <div className="energy-bar-mini">
              <div
                className="energy-fill-mini"
                style={{
                  width: `${Math.min(percentageUsed, 100)}%`,
                  background:
                    percentageUsed < 75
                      ? 'var(--liwaza-primary, #6750a4)'
                      : 'var(--liwaza-error, #ba1a1a)',
                }}
              />
            </div>

            {/* Expandable details toggle */}
            <button
              className="expand-toggle"
              onClick={() => setShowMinutesDetails(!showMinutesDetails)}
            >
              <span>
                {showMinutesDetails ? 'Hide details' : 'View details'}
              </span>
              {showMinutesDetails ? (
                <FiChevronUp size={16} />
              ) : (
                <FiChevronDown size={16} />
              )}
            </button>

            {/* Collapsible details section */}
            {showMinutesDetails && (
              <div className="minutes-details-expanded">
                <div className="details-section-header">
                    <span className="details-section-title">Today</span>
                    <span className="details-section-subtitle">
                      {getTimeUntilReset()}
                    </span>
                </div>
                <div className="details-grid">
                  <div className="detail-card">
                    <span className="detail-label">Used today</span>
                    <span className="detail-value">
                      {stats.sessionsUsedToday ?? stats.sessionsCount ?? 0}
                    </span>
                  </div>
                  <div className="detail-card">
                    <span className="detail-label">Sessions / day</span>
                    <span className="detail-value">{maxSessionsPerDay}</span>
                  </div>
                  <div className="detail-card">
                    <span className="detail-label">Max per session</span>
                    <span className="detail-value">
                      {formatMinutes(stats.maxSessionDuration)}
                    </span>
                  </div>
                  <div className="detail-card detail-card-muted">
                    <span className="detail-label">Reset</span>
                    <span className="detail-value">{getTimeUntilReset()}</span>
                  </div>
                </div>
                <div className="practice-note-mini">
                  <FiMessageCircle size={12} />
                  <span>Includes Speaking + Interview Sessions</span>
                </div>
              </div>
            )}

            {stats.activeSession && (
              <div className="active-session-indicator mini">
                <div className="pulse-dot"></div>
                <span>Session Active</span>
              </div>
            )}
          </div>
        ) : null}

        <div className="control-panel-divider" />

        {/* Ready to Start Checklist */}
        <div className="ready-checklist">
          <div className="checklist-header">
            <div className="checklist-title">
              <FiCheckCircle size={16} />
              <span>Ready to Start</span>
            </div>
            <div
              className={`checklist-status ${allChecksComplete ? 'complete' : ''}`}
            >
              {checksCount}/{totalChecks}
            </div>
          </div>

          {/* Permission warning */}
          {permissionStatus === 'denied' && !micTested && (
            <div className="permission-warning">
              <FiAlertTriangle size={16} />
              <div className="permission-warning-content">
                <strong>Microphone access required</strong>
                <span>
                  Please allow microphone access in your browser settings to
                  start speaking practice.
                </span>
              </div>
            </div>
          )}

          {/* Auto-checking indicator */}
          {!autoCheckComplete && (
            <div className="auto-check-indicator">
              <div className="auto-check-spinner"></div>
              <span>Checking devices...</span>
            </div>
          )}

          {audioInputDevices.length > 1 && (
            <div className="voice-input-device-picker">
              <label htmlFor="voice-input-device">Microphone</label>
              <select
                id="voice-input-device"
                value={selectedInputDeviceId}
                onChange={(event) =>
                  handleInputDeviceChange(event.target.value)
                }
                disabled={isRecording || isPlayingBack}
              >
                {audioInputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || 'Microphone'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div
            className="voice-audio-protection-note"
            title={getActiveAudioProtectionFeatures().join(', ')}
          >
            <FiCheckCircle size={14} />
            <span>
              <strong>Auto noise filtering:</strong>{' '}
              {getActiveAudioProtectionFeatures().join(' · ')}. Use a headset
              or push-to-talk if the room is busy.
            </span>
          </div>

          {noiseGuidance && (
            <div
              className={`voice-noise-guidance ${backgroundNoiseLevel === 'high' ? 'high' : 'moderate'}`}
            >
              <FiAlertTriangle size={16} />
              <div>
                <strong>{noiseGuidance.title}</strong>
                <span>{noiseGuidance.message}</span>
              </div>
            </div>
          )}

          <div className="checklist-items">
            <button
              type="button"
              className={`checklist-item checklist-action-item ${micTested ? 'checked' : permissionStatus === 'denied' ? 'error' : ''}`}
              onClick={testMicrophone}
              disabled={isRecording || isPlayingBack}
            >
              <div className="check-indicator">
                {micTested ? (
                  <FiCheck size={12} />
                ) : permissionStatus === 'denied' ? (
                  <FiX size={12} />
                ) : (
                  <span className="check-empty" />
                )}
              </div>
              <div className="preflight-btn-icon checklist-main-icon">
                <FiMic size={18} />
              </div>
              <div className="checklist-copy">
                <span className="checklist-item-title">
                  {isRecording
                    ? `Testing Microphone... ${recordingTimeLeft}s`
                    : isPlayingBack
                      ? 'Playing Microphone Test'
                      : 'Microphone'}
                </span>
                <span className="checklist-item-hint">
                  {isRecording
                    ? 'Stay quiet for 2s, then speak clearly for the sample.'
                    : isPlayingBack
                      ? 'Listen to your playback.'
                      : micTested
                        ? 'Tap to test again'
                        : 'Tap to check background noise and record a sample'}
                </span>
              </div>
              {micTested ? (
                <span className="check-label">Ready</span>
              ) : permissionStatus === 'denied' ? (
                <span className="check-label error">Blocked</span>
              ) : (
                <span className="checklist-action-text">Tap to test</span>
              )}
            </button>
            <button
              type="button"
              className={`checklist-item checklist-action-item ${audioTested ? 'checked' : ''}`}
              onClick={testAIVolume}
              disabled={isPlayingTestSound}
            >
              <div className="check-indicator">
                {audioTested ? (
                  <FiCheck size={12} />
                ) : (
                  <span className="check-empty" />
                )}
              </div>
              <div className="preflight-btn-icon checklist-main-icon">
                <FiVolume2 size={18} />
              </div>
              <div className="checklist-copy">
                <span className="checklist-item-title">
                  {isPlayingTestSound ? 'Testing Audio Output...' : 'Audio Output'}
                </span>
                <span className="checklist-item-hint">
                  {isPlayingTestSound
                    ? 'Listen for the test sound now.'
                    : audioTested
                      ? 'Tap to test again'
                      : 'Tap to play a short test sound'}
                </span>
              </div>
              {audioTested ? (
                <span className="check-label">Ready</span>
              ) : (
                <span className="checklist-action-text">Tap to test</span>
              )}
            </button>
            <div
              className={`checklist-item checklist-camera-item ${isCameraEnabled ? 'checked' : 'optional'}`}
            >
              <div className="check-indicator">
                {isCameraEnabled ? (
                  <FiCheck size={12} />
                ) : (
                  <span className="check-optional">–</span>
                )}
              </div>
              <div className="preflight-btn-icon checklist-main-icon">
                {isCameraEnabled ? (
                  <FiVideo size={18} />
                ) : (
                  <FiVideoOff size={18} />
                )}
              </div>
              <div className="checklist-copy">
                <span className="checklist-item-title">Camera</span>
                <span className="checklist-item-hint">
                  {isCameraEnabled
                    ? 'Preview enabled. Toggle off to keep camera off during the call.'
                    : 'Camera is off and will stay off during the call.'}
                </span>
              </div>
              <span className="check-label">
                {isCameraEnabled ? 'On' : 'Off'}
              </span>
              <div className="camera-toggle-wrapper">
                <input
                  type="checkbox"
                  id="camera-toggle"
                  checked={isCameraEnabled}
                  onChange={() => onCameraToggle(!isCameraEnabled)}
                />
                <label htmlFor="camera-toggle" className="toggle-switch">
                  <span className="toggle-slider-new"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Upgrade Modal */}
      {showUpgradeModal && (
        <div
          className="liwaza-modal-overlay"
          onClick={(e) => {
            // Only close if clicking on the overlay itself
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div
            className="liwaza-modal upgrade-modal gaming-modal"
            onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
          >
            <div className="upgrade-modal-header gaming-header">
              <div className="modal-title-section">
                <FiZap className="modal-icon" />
                <h3>Upgrade Your Practice Plan</h3>
              </div>
              <button className="modal-close gaming-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="upgrade-modal-content">
              <div className="current-plan gaming-current">
                <div className="plan-badge">
                  <FiTarget size={16} />
                  <span>Current: {upgradeInfo.currentPlan}</span>
                </div>
                <div className="current-plan-limits">
                  <div className="limit-item">
                    <FiClock size={14} />
                    <span>
                      {maxSessionsPerDay} sessions / day
                    </span>
                  </div>
                  <div className="limit-item">
                    <FiZap size={14} />
                    <span>
                      {formatMinutes(stats.maxSessionLimit)} per
                      session
                    </span>
                  </div>
                </div>
                <div className="energy-boost-note">
                  💼 Practice includes both Speaking & Interview sessions
                </div>
              </div>

              <div className="upgrade-options gaming-options">
                {upgradeInfo.upgradeOptions.map((plan, index) => (
                  <div key={index} className="upgrade-plan gaming-plan">
                    <div className="plan-glow"></div>
                    <div className="plan-header">
                      <div className="plan-title">
                        <FiAward size={18} />
                        <h4>{plan.name}</h4>
                      </div>
                      <div className="plan-price gaming-price">
                        {plan.price}
                      </div>
                    </div>
                    <div className="plan-features gaming-features">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="plan-feature gaming-feature">
                          <FiZap size={14} className="feature-icon" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn-lwz-primary plan-select-btn gaming-select"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle plan selection here
                        console.log(`Selected plan: ${plan.name}`);
                        // You can add actual subscription logic here
                      }}
                    >
                      <FiZap size={16} />
                      <span>Select {plan.name}</span>
                      <FiArrowUpRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="upgrade-modal-footer gaming-footer">
              <div className="footer-note">
                📚 More time for Speaking + Interview practice sessions
              </div>
              <button
                className="btn-lwz-secondary gaming-later"
                onClick={closeModal}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VoiceMinutesWidget;
